import {Component, type ReactNode} from "react";
import {Loader2Icon} from "lucide-react";
import {Button} from "@/components/ui/button.tsx";
import {InitialLoadContext} from "./InitialLoadContext.ts";

type Props = {
    children: ReactNode;
    onReady?: () => void;
    onError?: () => void;
};

type State = {ready: boolean; error?: Error; attempt: number};

/** Owns the initial loading UI and retries by remounting its provider tree. */
export default class InitialLoadBoundary extends Component<Props, State> {
    state: State = {ready: false, attempt: 0};

    static getDerivedStateFromError(error: Error): Partial<State> {
        return {error};
    }

    componentDidCatch() {
        this.props.onError?.();
    }

    componentDidUpdate(_previousProps: Props, previousState: State) {
        if (!this.state.error && this.state.ready && !previousState.ready) this.props.onReady?.();
    }

    private markReady = () => {
        if (!this.state.ready) this.setState({ready: true});
    };

    private retry = () => {
        this.setState(state => ({ready: false, error: undefined, attempt: state.attempt + 1}));
    };

    render() {
        if (this.state.error) {
            return <main className="grid size-full place-content-center gap-3 text-center" role="alert">
                <p>Encounter konnte nicht vollständig geladen werden.</p>
                <p className="text-sm text-muted-foreground">{this.state.error.message}</p>
                <Button variant="outline" onClick={this.retry}>Erneut versuchen</Button>
            </main>;
        }

        return <div className="relative size-full min-h-0">
            <InitialLoadContext.Provider key={this.state.attempt} value={this.markReady}>
                {this.props.children}
            </InitialLoadContext.Provider>
            {!this.state.ready && <main
                className="absolute inset-0 grid place-content-center bg-background"
                role="status" aria-label="Encounter wird geladen"
            >
                <Loader2Icon className="size-16 animate-spin"/>
            </main>}
        </div>;
    }
}
